// ══════════════════════════════════════════════════════════════════════════════
// PAGE MES PUBLICATIONS - Utilise le Modal de Création Réutilisable
// Types: Article Blog, Photo, Vidéo, Réel, Avant/Après
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Plus, Search, Heart, Eye,
  ChevronRight, Clock, Trash2, Image, Video,
  RefreshCw, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import CreatePublicationModal from '../../../components/publish/CreatePublicationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE MES PUBLICATIONS
// ══════════════════════════════════════════════════════════════════════════════
export default function MesPublications({ onAskAbra }) {
  const { user, token } = useCurrentUser();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Tous');
  const [filterCounts, setFilterCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const filters = ['Tous', 'Articles Blog', 'Photos', 'Vidéos', 'Réels', 'Avant/Après', 'Brouillons'];
  
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const loadPosts = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/my-posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const postsData = data.posts || data || [];
        setPosts(postsData);
        
        const counts = {
          'Tous': postsData.length,
          'Articles Blog': postsData.filter(p => p.post_type === 'article_blog' || p.post_type === 'article').length,
          'Photos': postsData.filter(p => p.post_type === 'photo').length,
          'Vidéos': postsData.filter(p => p.post_type === 'video').length,
          'Réels': postsData.filter(p => p.post_type === 'reel').length,
          'Avant/Après': postsData.filter(p => p.post_type === 'avant_apres').length,
          'Brouillons': postsData.filter(p => p.status === 'draft').length,
        };
        setFilterCounts(counts);
      }
    } catch (e) {
      console.error('Error loading posts:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/posts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setPosts(prev => prev.filter(p => p.id !== id));
        toast.success('Publication supprimée');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = (post.content || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (post.title || '').toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    
    switch (activeFilter) {
      case 'Articles Blog': return post.post_type === 'article_blog' || post.post_type === 'article';
      case 'Photos': return post.post_type === 'photo';
      case 'Vidéos': return post.post_type === 'video';
      case 'Réels': return post.post_type === 'reel';
      case 'Avant/Après': return post.post_type === 'avant_apres';
      case 'Brouillons': return post.status === 'draft';
      default: return true;
    }
  });

  const totalLikes = posts.reduce((acc, p) => acc + (p.likes_count || 0), 0);
  const totalViews = posts.reduce((acc, p) => acc + (p.views_count || 0), 0);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={18} className="text-purple-500" />;
      case 'reel': return <Smartphone size={18} className="text-pink-500" />;
      case 'photo': return <Image size={18} className="text-green-500" />;
      case 'avant_apres': return <RefreshCw size={18} className="text-orange-500" />;
      default: return <FileText size={18} className="text-blue-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'article_blog':
      case 'article': return 'Article Blog';
      case 'video': return 'Vidéo';
      case 'reel': return 'Réel';
      case 'photo': return 'Photo';
      case 'avant_apres': return 'Avant/Après';
      default: return type;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes publications</h1>
            <p className="text-sm text-gray-500">Gère et partage tes contenus</p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            data-testid="create-publication-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Nouvelle</span>
          </Button>
        </div>

        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <FileText size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">{posts.length} pub.</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Heart size={14} className="text-red-500" />
            <span className="text-xs font-medium text-gray-600">{totalLikes}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Eye size={14} className="text-blue-500" />
            <span className="text-xs font-medium text-gray-600">{totalViews}</span>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-3">
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-gray-100">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-900 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Filtres */}
      <div className="flex-shrink-0 pb-3">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none' }}
          >
            {filters.map((filter) => {
              const count = filterCounts[filter] || 0;
              return (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    activeFilter === filter
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {filter}
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center text-[10px] rounded-full ${
                        activeFilter === filter ? 'bg-white/25 text-white' : 'bg-orange-500 text-white'
                      }`}>
                        {count}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          {showScrollHint && (
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end pr-1">
              <ChevronRight size={14} className="text-orange-500 animate-pulse" />
            </div>
          )}
        </div>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-hidden rounded-xl bg-white border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-4">
              <FileText size={28} className="text-orange-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Aucune publication</h3>
            <p className="text-sm text-gray-500 mb-4">Crée ta première publication pour la voir ici</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
            >
              <Plus size={16} className="mr-2" />
              Créer une publication
            </Button>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-2 space-y-2">
            {filteredPosts.map((post) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-50 rounded-xl overflow-hidden hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start gap-3 p-3">
                  {/* Thumbnail */}
                  {(post.media_url || post.before_media_url || post.cover_image_url) ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                      <img
                        src={post.media_url || post.before_media_url || post.cover_image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                      {getTypeIcon(post.post_type)}
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium">
                        {getTypeLabel(post.post_type)}
                      </span>
                      {post.status === 'draft' && (
                        <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-[10px] font-medium">
                          Brouillon
                        </span>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 text-sm truncate">
                      {post.title || post.content?.substring(0, 50) || 'Sans titre'}
                    </h3>
                    
                    {post.content && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {post.content.substring(0, 80)}...
                      </p>
                    )}
                    
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {post.metier && (
                        <span className="text-orange-500 font-medium">
                          {post.metier}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Eye size={12} />
                        {post.views_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart size={12} className="text-red-500" />
                        {post.likes_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="p-2 rounded-lg hover:bg-red-50"
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création - Composant réutilisable */}
      <CreatePublicationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadPosts}
        token={token}
        onAskAbra={onAskAbra}
      />
    </div>
  );
}
